import * as Sentry from "@sentry/react";
import {
    ISchema,
    SchemaDescription,
    ArraySchema,
    Flags,
    Maybe,
    AnyObject,
    ValidationError,
} from "yup";

import { assertNever } from "../../obj";
import { toSplitAtomValidationError } from "../splitFormAtom";

export function extractRequired(schema: ISchema<unknown>): boolean | undefined {
    const description = schema.describe();
    return "tests" in description
        ? description.tests.map((test) => test.name).includes("required")
        : false;
}

export function extractStringMinLength(
    schema: ISchema<string>
): number | undefined {
    const description = schema.describe();
    const minLength =
        "tests" in description
            ? description.tests.find((test) => test.name === "min")?.params?.min
            : undefined;

    return typeof minLength === "number" ? minLength : undefined;
}

export function extractStringMaxLength(
    schema: ISchema<string>
): number | undefined {
    const description = schema.describe();
    const maxLength =
        "tests" in description
            ? description.tests.find((test) => test.name === "max")?.params?.max
            : undefined;

    return typeof maxLength === "number" ? maxLength : undefined;
}

export function extractStringPattern(
    schema: ISchema<string>
): string | undefined {
    const description = schema.describe();
    const pattern =
        "tests" in description
            ? description.tests.find((test) => test.name === "matches")?.params
                  ?.regex
            : undefined;
    return pattern instanceof RegExp ? pattern.toString() : undefined;
}

export function extractNumberMin(
    schema: ISchema<number | null>
): number | undefined {
    const description = schema.describe();
    const min =
        "tests" in description
            ? description.tests.find((test) => test.name === "min")?.params?.min
            : undefined;

    return typeof min === "number" ? min : undefined;
}

export function extractNumberMax(
    schema: ISchema<number | null>
): number | undefined {
    const description = schema.describe();
    const max =
        "tests" in description
            ? description.tests.find((test) => test.name === "max")?.params?.max
            : undefined;

    return typeof max === "number" ? max : undefined;
}

export function extractIntegerPrecision(
    schema: ISchema<number | null>
): 0 | undefined {
    const description = schema.describe();
    const integerInTests =
        "tests" in description
            ? description.tests.map((test) => test.name).includes("integer")
            : false;
    return integerInTests ? 0 : undefined;
}

export function extractNullable(schema: ISchema<unknown>): boolean {
    const description = schema.describe() as SchemaDescription;
    return description.nullable;
}

/**
 * An array schema that validates all items in parallel.
 * Also flags errors with `toSplitAtomValidationError`, so `splitFormAtom` can propagate them to the correct form atom.
 *
 * @extends ArraySchema the `Yup.array()` underlying schema
 */
export class ParallelArraySchema<
    TIn extends any[] | null | undefined,
    TContext,
    TDefault = undefined,
    TFlags extends Flags = "",
> extends ArraySchema<TIn, TContext, TDefault, TFlags> {
    protected _validate(
        ...[_value, options, panic, next]: Parameters<
            ArraySchema<TIn, TContext, TDefault, TFlags>["_validate"]
        >
    ) {
        const innerType = this.innerType;
        const recursive = options?.recursive ?? this.spec.recursive;

        // Forbid recursive validation on the parent validator, leaving only the full array tests to the parent
        super._validate(
            _value,
            { ...options, recursive: false },
            panic,
            (arrayErrors, value) => {
                if (!recursive || !innerType || !this._typeCheck(value)) {
                    next(arrayErrors, value);
                    return;
                }

                const promises: Promise<ErrorAction>[] = new Array(
                    value.length
                );
                for (let index = 0; index < value.length; index++) {
                    promises[index] = new Promise((resolve) => {
                        this.runTests(
                            {
                                value,
                                tests: [
                                    innerType.asNestedTest({
                                        options: options ?? {},
                                        index,
                                        parent: value,
                                        parentPath: options?.path,
                                        originalParent:
                                            options?.originalValue ?? _value,
                                    }),
                                ],
                                originalValue: options?.originalValue ?? _value,
                                options: options ?? {},
                            },
                            (err) =>
                                resolve({
                                    type: "panic",
                                    error: toValidationError(err),
                                    index,
                                }),
                            (innerTypeErrors) =>
                                resolve({
                                    type: "next",
                                    errors: innerTypeErrors,
                                    index,
                                })
                        );
                    });
                }
                Promise.all(promises).then((actions) => {
                    const { shouldPanic, errors, indices } = actions.reduce(
                        ({ shouldPanic, errors, indices }, action) => {
                            switch (action.type) {
                                case "next":
                                    return {
                                        shouldPanic,
                                        errors: shouldPanic
                                            ? errors
                                            : errors.concat(action.errors),
                                        indices: shouldPanic
                                            ? indices
                                            : {
                                                  ...indices,
                                                  [action.index]:
                                                      new ValidationError(
                                                          action.errors
                                                      ).message,
                                              },
                                    };
                                case "panic":
                                    return {
                                        shouldPanic: true,
                                        errors: shouldPanic
                                            ? errors.concat(action.error)
                                            : [action.error],
                                        indices: shouldPanic
                                            ? {
                                                  ...indices,
                                                  [action.index]: trimIndex(
                                                      action.error.message,
                                                      action.index
                                                  ),
                                              }
                                            : {
                                                  [action.index]: trimIndex(
                                                      action.error.message,
                                                      action.index
                                                  ),
                                              },
                                    };
                                default:
                                    return assertNever(action);
                            }
                        },
                        {
                            shouldPanic: false,
                            errors: [] as ValidationError[],
                            indices: {},
                        }
                    );

                    if (shouldPanic) {
                        panic(
                            toSplitAtomValidationError(
                                new ValidationError(errors),
                                indices
                            ),
                            value
                        );
                    } else {
                        const validationErrors = errors.length
                            ? [
                                  toSplitAtomValidationError(
                                      new ValidationError(errors),
                                      indices
                                  ),
                              ]
                            : [];
                        next(validationErrors.concat(arrayErrors), value);
                    }
                });
            }
        );
    }
}

type ErrorAction =
    | { type: "next"; errors: ValidationError[]; index: number }
    | { type: "panic"; error: ValidationError; index: number };

function toValidationError(error: Error): ValidationError {
    if (!ValidationError.isError(error)) {
        Sentry.captureMessage("Expected a ValidationError", {
            extra: { error },
        });
        return new ValidationError(error.message);
    }
    return error;
}

function trimIndex(message: string, index: number) {
    const expectedPrefix = `[${index}].`;
    if (message.startsWith(expectedPrefix)) {
        return message.slice(expectedPrefix.length);
    } else {
        return message;
    }
}

/**
 * Create a parallel array schema.
 * Equivalent to `Yup.array()`, but validates all items (the `.of(validator)` part of the schema) in parallel.
 */
export function parallelArray<C extends Maybe<AnyObject> = AnyObject, T = any>(
    type?: ISchema<T, C>
) {
    return new ParallelArraySchema<T[] | undefined, C>(type);
}
